import React from "react";
import { Card, Skeleton, Typography } from "@mui/material";
import Grid from "@mui/material/GridLegacy";
import { usePageContents } from "../hooks/usePageContents";
import { PageContents } from "../DTOs/responses/pageContents";
import HtmlContentWrapper from "./HtmlContentWrapper";
import parse from "html-react-parser";

interface InfoCardProps {
  contents?: PageContents;
  backgroundImage?: string;
  reverse?: boolean;
  tone?: "dark" | "deep";
}

const CardRow: React.FC<InfoCardProps> = ({
  contents,
  backgroundImage,
  reverse,
  tone = "dark",
}) => {
  const textBg = tone === "deep" ? "#E7F0EE" : "#FAF8F3";
  const accent = tone === "deep" ? "#2E7D78" : "#C46A2D";
  const titleColor = tone === "deep" ? "#1F4F4B" : "#20242A";
  const bodyColor = tone === "deep" ? "#355F5B" : "#62605A";

  return (
    <Grid
      container
      direction={reverse ? "row-reverse" : "row"}
      alignItems="stretch"
      sx={{
        minHeight: { xs: "auto", md: "420px" },
        overflow: "hidden",
      }}
    >
      <Grid
        item
        xs={12}
        md={7}
        sx={{
          px: { xs: "28px", md: "72px" },
          py: { xs: "26px", md: "64px" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          backgroundColor: textBg,
          color: titleColor,
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            bottom: 0,
            width: "4px",
            background: accent,
          },
        }}
      >
        {contents ? (
          <>
            <Typography
              fontFamily="Source Sans Pro SemiBold"
              fontSize={{ xs: 20, sm: 22, md: 24, lg: 28 }}
              lineHeight={1.2}
              color={titleColor}
            >
              {contents.title}
            </Typography>
            <Typography
              variant="body1"
              component="div"
              fontSize={{ xs: 13, sm: 14, md: 15 }}
              lineHeight={1.55}
              color={bodyColor}
              sx={{
                mt: 2,
                "& p": {
                  margin: "0 0 14px",
                },
                "& p:last-of-type": {
                  marginBottom: 0,
                },
              }}
            >
              <HtmlContentWrapper>{parse(contents.text)}</HtmlContentWrapper>
            </Typography>
          </>
        ) : (
          <>
            <Skeleton variant="text" width="60%" height={32} />
            <Skeleton variant="text" width="100%" height={22} />
            <Skeleton variant="text" width="90%" height={22} />
          </>
        )}
      </Grid>

      <Grid
        item
        xs={12}
        md={5}
        sx={{
          position: "relative",
          overflow: "hidden",
          minHeight: { xs: 220, md: "auto" },
          "&::before": {
            content: '""',
            position: "absolute",
            inset: "-4px",
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "saturate(92%) contrast(96%)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              tone === "deep"
                ? "linear-gradient(135deg, rgba(46,125,120,0.36), rgba(32,36,42,0.24))"
                : "linear-gradient(135deg, rgba(196,106,45,0.14), rgba(32,36,42,0.24))",
          },
        }}
      />
    </Grid>
  );
};

const MissionVisionValues: React.FC = () => {
  const { data } = usePageContents("0d994e82-dac8-424a-b428-35d21131996d");

  const rows: Array<{
    order: number;
    image: string;
    reverse: boolean;
    tone: "dark" | "deep";
  }> = [
    {
      order: 1,
      image: "/home-carousel-2.jpg",
      reverse: false,
      tone: "dark",
    },
    {
      order: 2,
      image: "/home-carousel-3.jpg",
      reverse: true,
      tone: "deep",
    },
    {
      order: 3,
      image: "/school-outdoors.jpg",
      reverse: false,
      tone: "dark",
    },
  ];

  return (
    <Card
      sx={{
        mt: -4,
        mb: 10,
        mx: "auto",
        width: "90%",
        maxWidth: "1500px",
        overflow: "hidden",
        backgroundColor: "transparent",
        border: "1px solid #E4DED2",
        boxShadow: "0 18px 42px rgba(32, 36, 42, 0.10)",
      }}
    >
      {rows.map((row, index) => (
        <CardRow
          key={row.order}
          backgroundImage={row.image}
          reverse={index % 2 === 1 ? true : false}
          tone={row.tone}
          contents={data?.find((content) => content.order === row.order)}
        />
      ))}
    </Card>
  );
};

export default MissionVisionValues;
